import { ABMGenerico } from "src/general_components/ABMGenerico";
import RestriccionesPorPerfil from "src/general_components/PermisosPerfiles";
import IconButton from '@mui/material/IconButton'; // Botón para íconos con funcionalidad de clic
import Tooltip from '@mui/material/Tooltip'; // Para mostrar el tooltip sobre el ícono
import { Grid } from "@mui/material";
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import { SimpleDialogComponent } from "src/general_components/SimpleDialogComponent";
import { useState, useEffect } from "react";
import { getRolesMenu, getMenu, postRolesMenu } from 'src/general_helpers/Menu';
import { SnackbarMessenger } from "src/general_components/SnackbarMessenger";

export const RolesPerfiles = ({ id_perfil }) => {
    const modelo = "roles_perfiles";

    // Estados para gestionar la lógica del componente
    const [modalState, setModalState] = useState(false);
    const [menu_list, setMenuList] = useState([]);
    const [roles_menu, setRolesMenu] = useState([]);
    const [id_roles, setIdROles] = useState(false);
    const [insertData, setInsertData] = useState(false);
    const [datos_insert, setDatosInsert] = useState([]);
    const [message, setMessage] = useState("");
    const [severity, setSeverity] = useState("success");
    const [snackbarVisible, setSnackbarVisible] = useState(false);

    // Componente hijo para renderizar dentro del modal
    let dialogChild = <RestriccionesPorPerfil menu={menu_list} roles_menu={roles_menu} id_roles={id_roles} setDatosInsert={setDatosInsert}></RestriccionesPorPerfil>

    /**********************************************************************
        🌟 Gestiona el evento de clic en el botón de una fila de la tabla.
        Recibe como parámetro los datos de la fila seleccionada y realiza 
        las siguientes acciones:
        - Establece el ID del rol en el estado local.
        - Obtiene los permisos asociados al rol.
        - Abre el modal para administrar funcionalidades.
    **********************************************************************/
    const handleButtonClick = async (rowData) => {
        console.log("rowData",rowData);
        setIdROles(rowData.id_roles);
        await obtenerDatosPermisos(rowData.id_roles);
        toggleModal();
    };

    /**********************************************************************
        🌟 Obtiene los permisos asociados a un rol específico.
        Recibe el ID del rol como parámetro, realiza una consulta a la API 
        y actualiza el estado con los datos obtenidos.
    **********************************************************************/
    const obtenerDatosPermisos = async (rol) => {
        let response_roles_menu = await getRolesMenu(rol);
        if (response_roles_menu.length > 0) {
            response_roles_menu = response_roles_menu[0].relations?.roles_menu ? response_roles_menu[0].relations.roles_menu : [];
            setRolesMenu(response_roles_menu);
        }
    };

    /**********************************************************************
        🌟 Obtiene la lista de menús disponibles y transforma su estructura 
        para que sea compatible con el componente.
    **********************************************************************/
    const obtenerMenus = async () => {
        const response_menu_list = transformar_menu_list(await getMenu());
        setMenuList(response_menu_list);
    };

    /**********************************************************************
        🌟 Transforma la lista de menús obtenida desde la API.
        Cambia las claves de los objetos y añade datos adicionales como 
        la jerarquía y el módulo al que pertenece cada menú.
    **********************************************************************/
    const transformar_menu_list = (data) => {
        return data.map(item => ({
            level: item.jerarquia, // Asignar la jerarquía a level
            name: item.detalle, // Usar detalle como name
            module: item.relations?.modulos?.nombre || "", // Obtener el nombre del módulo desde relations.modulos.nombre
            ...item, // Guardar el objeto original por si es necesario acceder a más datos
        }));
    };

    /**********************************************************************
        🌟 Define una columna adicional para la tabla de datos con el campo 
        "columna_extra_1", que muestra un ícono de módulo y un tooltip 
        para administrar permisos. Al hacer clic en el ícono, se ejecuta 
        `handleButtonClick` con la fila seleccionada como parámetro.
    **********************************************************************/
    const columnasAdicional = [{
        field: "columna_extra_1",
        headerName: "Funcionalidades",
        flex: 1,
        headerAlign: "center",
        renderCell: (params) => (
            <Grid style={{ alignItems: 'center', textAlign: 'center', margin: 'auto' }}>
                <Tooltip title="Administrar Funcionalidades">
                    <IconButton onClick={() => handleButtonClick(params.row)}>
                        <ViewModuleIcon />
                    </IconButton>
                </Tooltip>
            </Grid>
        )
    }];

    /**********************************************************************
        🌟 Condiciones para filtrar los datos de la tabla según el perfil 
        seleccionado.
    **********************************************************************/
    const condiciones = [
        { propiedad: "id_perfiles", valor: id_perfil, operador: "=" }
    ];

    /**********************************************************************
        🌟 Alterna el estado del modal para abrirlo o cerrarlo.
    **********************************************************************/
    const toggleModal = () => {
        setModalState(!modalState);
    };

    /**********************************************************************
        🌟 Confirma la inserción de datos, activando la lógica correspondiente.
    **********************************************************************/
    const confirmar = () => {
        setInsertData(!insertData);
    };

    /**********************************************************************
        🌟 Hook para obtener la lista de menús al cargar el componente.
    **********************************************************************/
    useEffect(() => {
        const fetchMenus = async () => {
            try {
                await obtenerMenus();
            } catch (error) {
                console.error("Error fetching menus:", error);
            }
        };
        fetchMenus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**********************************************************************
        🌟 Valores por defecto para el formulario de ABMGenerico.
    **********************************************************************/
    const valuesDefaultForm = { id_perfiles: id_perfil.toString() };

    /**********************************************************************
        🌟 Transforma la estructura del objeto de datos para facilitar la 
        inserción en la base de datos.
    **********************************************************************/
    function transformarEstructura(data) {
        const result = {};
        for (const key in data) {
            if (data.hasOwnProperty(key)) {
                const item = data[key];
                if (item.items) {
                    for (const subKey in item.items) {
                        if (item.items.hasOwnProperty(subKey)) {
                            result[subKey] = item.items[subKey];
                        }
                    }
                    delete item.items;
                }
                result[key] = item;
            }
        }
        return result;
    }

    let id_owner = JSON.parse(localStorage.getItem("siam-user-data"))?.id_owner;

    /**********************************************************************
        🌟 Transforma los datos en un formato compatible con la API para 
        insertar roles y menús.
    **********************************************************************/
    function transformData(inputData) {
        const transformedArray = [];
        const idOwner = id_owner || "29"; // Fijar el valor constante de id_owner
        const idMenuAccionesTipo = "1"; // Fijar el valor constante de id_menu_acciones_tipo

        for (const key in inputData) {
            transformedArray.push({
                id_menu: inputData[key].id_menu,
                id_roles: inputData[key].id_roles,
                id_menu_acciones_tipo: inputData[key].activo ? idMenuAccionesTipo : "2",
                id_owner: idOwner,
                id: inputData[key].id,
            });
        }
        return transformedArray;
    }

    /**********************************************************************
        🌟 Hook para insertar datos en la base de datos cuando `insertData` 
        cambia de estado.
    **********************************************************************/
    useEffect(() => {
        const fetchInsertRolesMenu = async () => {
            try {
                if (insertData) {
                    const datos = transformarEstructura(datos_insert);
                    const data_insert_final = transformData(datos);
                    setMessage("Datos insertados correctamente");
                    setSeverity("success");
                    setSnackbarVisible(true);
                    await postRolesMenu(data_insert_final);
                    confirmar();
                }
            } catch (error) {
                console.error("Error fetching menus:", error);
            }
        };
        fetchInsertRolesMenu();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [insertData]);

    return (
        <>
            <ABMGenerico modelo={modelo} titulo={"Roles"} columnasAdicional={columnasAdicional} condiciones={condiciones} valuesDefaultForm={valuesDefaultForm} />
            <SimpleDialogComponent
                open={modalState}
                modalName={"Administrador de Funcionalidades"}
                child={dialogChild}
                handleClose={toggleModal}
                buttonEnviar={true}
                buttonCancelar={true}
                maxWidth="lg"
                setPushData={confirmar}
            />
            <SnackbarMessenger
                open={snackbarVisible}
                message={message}
                severity={severity}
                close={() => {
                    setSnackbarVisible(false);
                }}
            />
        </>
    );
};
